package es.subastia.data.repository

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.FieldValue
import dev.gitlive.firebase.firestore.firestore
import es.subastia.data.mapper.toDomain
import es.subastia.data.model.AuctionDataModel
import es.subastia.domain.model.AuctionDomainModel
import es.subastia.domain.repository.FavoritesRepository
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map

class FavoritesRepositoryImpl : FavoritesRepository {
    private val firestore = Firebase.firestore

    override fun isFavorite(userId: String, auctionId: String): Flow<Boolean> {
        return firestore.collection("users").document(userId)
            .collection("favorites").document(auctionId)
            .snapshots.map { it.exists }
    }

    override suspend fun toggleFavorite(userId: String, auctionId: String) {
        val doc = firestore.collection("users").document(userId)
            .collection("favorites").document(auctionId)
        
        if (doc.get().exists) {
            doc.delete()
        } else {
            // Use serverTimestamp for better sorting later
            doc.set(mapOf("addedAt" to FieldValue.serverTimestamp))
        }
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun getFavorites(userId: String): Flow<List<AuctionDomainModel>> {
        return firestore.collection("users").document(userId)
            .collection("favorites").snapshots.flatMapLatest { snapshot ->
                val ids = snapshot.documents.map { it.id }
                if (ids.isEmpty()) {
                    kotlinx.coroutines.flow.flowOf(emptyList())
                } else {
                    // Fetch all auctions with these IDs
                    // Note: This is an expensive operation if many favorites exist.
                    // In a production app, we'd use Firestore 'in' query or cache.
                    flow {
                        val auctions = ids.mapNotNull { id ->
                            try {
                                val auctionDoc = firestore.collection("auctions").document(id).get()
                                if (auctionDoc.exists) {
                                    auctionDoc.data<AuctionDataModel>().toDomain()
                                } else null
                            } catch (e: Exception) {
                                null
                            }
                        }
                        emit(auctions)
                    }
                }
            }
    }
}
